import dynamic from 'next/dynamic'
import React, { useEffect, useState, useMemo } from 'react'

import { Banner } from './Banner'
import { Ctas } from './Ctas'
import { VehicleDeals } from './VehicleDeals'
import { Stats } from './Stats'
import { Content } from './Content'
import { Faqs, FaqItem } from './Faqs'

import { INITIAL_STATE } from '@/store/reducers/search'
import { submitSearchService } from '@/lib/services/searchService'
import { getVehicleTechnicalDataService } from '@/lib/services/vehicleService'
import { capitaliseFirstLetter } from '@/lib/utilities/search'

const Colour = dynamic(() => import('./Colour'), {
  ssr: false,
})

import styles from './styles.module.scss'

export type VehicleModelContent = {
  heroSectionImage?: string
  firstCarouselBestDeals?: string
  secondCarouselInStock?: string
  firstBodyContent?: string
  performanceSectionImage?: string
  secondBodyContent?: string
  smallParagraph?: string
  smallParagraphImage?: string
}

export type MakeModelLandingProps = {
  isVans: boolean
  vehicleModelContent: VehicleModelContent
  range: {
    id: string
    manufacturer: string
    manufacturerName: string
    name: string
  }
  faqList: FaqItem[]
  supplierCount: number
}

export const MakeModelLanding = ({
  isVans,
  vehicleModelContent,
  range,
  faqList,
  supplierCount,
}: MakeModelLandingProps) => {
  const {
    manufacturer,
    manufacturerName,
    id: rangeId,
    name: rangeName,
  } = range

  const [dealList, setDealList] = useState([])
  const [secondDealList, setSecondDealList] = useState([])
  const [technicalData, setTechnicalData] = useState(null)

  const vehicle = useMemo(() => dealList?.[0]?.vehicle, [dealList])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [allDealsResponse, inStockDealsResponse] = await Promise.all([
          submitSearchService({
            ...INITIAL_STATE.searchParameters,
            manufacturers: [manufacturer],
            ranges: [rangeId],
            grouping: 1,
          }),
          submitSearchService({
            ...INITIAL_STATE.searchParameters,
            manufacturers: [manufacturer],
            ranges: [rangeId],
            grouping: 1,
            inStock: 1,
          }),
        ])

        const allDeals = allDealsResponse?.results || []
        const inStockDeals = inStockDealsResponse?.results || []

        setDealList(allDeals)
        setSecondDealList(inStockDeals)

        if (allDeals.length > 0 && allDeals[0]?.vehicle?.id) {
          const technicalResponse = await getVehicleTechnicalDataService({
            id: allDeals[0].vehicle.id,
          })
          setTechnicalData(technicalResponse)
        }
      } catch (err) {
        console.error('Failed to fetch data:', err)
        setDealList([])
        setSecondDealList([])
        setTechnicalData(null)
      }
    }

    fetchData()
  }, [manufacturer, manufacturerName, rangeId, rangeName])

  return (
    <div className={styles.page}>
      <Banner
        vehicleModelContent={vehicleModelContent}
        range={range}
      />
      <Ctas supplierCount={supplierCount} />
      <div className={styles.deals}>
        <div className={styles.container}>
          <h2>Best deals for the {capitaliseFirstLetter(manufacturerName)} {capitaliseFirstLetter(rangeName)}</h2>
        </div>
        <VehicleDeals
          isVans={isVans}
          title="Leasing deals by best value"
          content={vehicleModelContent.firstCarouselBestDeals}
          dealList={dealList}
        />
        <VehicleDeals
          isVans={isVans}
          title="In stock lease deals"
          content={vehicleModelContent.secondCarouselInStock}
          dealList={secondDealList}
        />
      </div>
      {technicalData && vehicle && (
        <Stats
          vehicleModelContent={vehicleModelContent}
          technicalData={technicalData}
          vehicle={vehicle}
        />
      )}
      <Content vehicleModelContent={vehicleModelContent} />
      {dealList.length && (
        <Colour
          vehicle={vehicle}
          vehiclePrice={dealList[0].vehiclePrice}
        />
      )}
      {faqList && (
        <Faqs faqList={faqList} />
      )}

    </div>
  )
}
